import React from 'react'
import copy from 'copy-to-clipboard'
import { routes } from '@sketch/modules-common'
import {
  Box,
  useResponsiveDropdown,
  Dropdown,
  DropdownItem,
  useModalContext,
  LoadingPlaceholder,
  Flex,
  Tooltip,
  Text,
} from '@sketch/components'
import { useToast } from '@sketch/toasts'
import SharingModal from '../../modals/SharingModal'
import { PartialShareModal } from '../../modals/PartialShareModal'
import {
  TriggerButton,
  DropdownHeader,
  DropdownItemDescription,
  PlusIcon,
  DropdownIcon,
  PreviewHeader,
  LinkIcon,
  SlidersIcon,
  ActionsWrapper,
  ActionButton,
  PreviewItem,
  PreviewList,
  ItemName,
  NewButton,
  ErrorIcon,
  TryAgainButton,
  ExpiredHeader,
  ExpiredItemName,
} from './PartialShareDropdown.styles'
import { ShareWithoutVersion, useVersioning } from '../../../versioning'
import {
  useGetSharePreviewsQuery,
  GetSharePreviewsQuery,
} from '@sketch/gql-types'

interface DropdownContentProps {
  share: ShareWithoutVersion
  hide: (trigger: string) => void
}

type Preview = NonNullable<
  NonNullable<GetSharePreviewsQuery['share']>['previews'][0]
>

const separatePreviewsByExpiry = (
  previewsList: (Preview | null)[]
): { active: Preview[]; expired: Preview[] } => {
  const now = new Date().getTime()

  // Separate into active and expired
  const separated = previewsList.reduce(
    (acc, preview) => {
      if (!preview) return acc

      const isExpired =
        preview.expiryDate && new Date(preview.expiryDate).getTime() < now

      if (isExpired) {
        acc.expired.push(preview)
      } else {
        acc.active.push(preview)
      }

      return acc
    },
    { active: [] as Preview[], expired: [] as Preview[] }
  )

  // Sort function for previews by expiry date (most in the future first)
  const sortByExpiryDate = (a: Preview, b: Preview) => {
    // If neither has an expiry date, maintain original order
    if (!a.expiryDate && !b.expiryDate) return 0

    // If only one has an expiry date, prioritize the one with a date
    if (!a.expiryDate) return 1
    if (!b.expiryDate) return -1

    // Both have expiry dates, sort by date (most in the future first)
    const dateA = new Date(a.expiryDate).getTime()
    const dateB = new Date(b.expiryDate).getTime()

    return dateB - dateA // descending order (most future first)
  }

  // Sort both arrays
  separated.active.sort(sortByExpiryDate)
  separated.expired.sort(sortByExpiryDate)

  return separated
}

const PreviewArea = ({ share, hide }: DropdownContentProps) => {
  const { showModal } = useModalContext()
  const { showToast } = useToast()
  const { latestVersionId } = useVersioning()
  const { data, loading, error, refetch } = useGetSharePreviewsQuery({
    variables: { shareIdentifier: share.identifier },
    fetchPolicy: 'cache-and-network',
  })

  if (loading) {
    return (
      <Flex height={52} justifyContent="center">
        <LoadingPlaceholder size="24px" />
      </Flex>
    )
  }

  if (error) {
    return (
      <Flex
        justifyContent="center"
        alignItems="center"
        flexDirection="column"
        pl={5}
        pr={5}
      >
        <ErrorIcon />
        <Text mt={1} textStyle="copy.primary.standard.C">
          Failed to load previews
        </Text>
        <Text mt={1} textAlign="center" textStyle="copy.quaternary.standard.C">
          An error occurred while loading previews from this document
        </Text>
        <TryAgainButton onClick={refetch}>Try again</TryAgainButton>
      </Flex>
    )
  }

  const showPreviewCreationModal = () => {
    showModal(PartialShareModal, {
      shareIdentifier: share.identifier,
      shareName: share.name,
      versionShortId: latestVersionId,
    })
    hide('opened-modal')
  }

  if (!data || data?.share?.previews.length === 0) {
    return (
      <DropdownItem onClick={showPreviewCreationModal}>
        Share a Preview
        <DropdownItemDescription>
          Use previews to share only specific pages and prototypes.
        </DropdownItemDescription>
      </DropdownItem>
    )
  }

  const { active: activePreviews, expired: expiredPreviews } =
    separatePreviewsByExpiry(data.share?.previews || [])

  const handleCopyLink = (previewID: string) => {
    const previewPath = routes.PREVIEW_VIEW.create({ previewID })
    const fullUrl = `${window.location.origin}${previewPath}`
    copy(fullUrl)
    showToast('Link copied')
  }

  const handleEdit = (existingPreviewId: string) => {
    showModal(PartialShareModal, {
      shareIdentifier: share.identifier,
      shareName: share.name,
      versionShortId: latestVersionId,
      existingPreviewId,
    })
  }

  const previewLength = data.share?.previews.length ?? 0

  return (
    <>
      <PreviewHeader>
        <span>{previewLength} Previews</span>
        <Tooltip placement="top" content="Share a Preview…">
          <NewButton onClick={showPreviewCreationModal}>
            <PlusIcon />
          </NewButton>
        </Tooltip>
      </PreviewHeader>
      <PreviewList>
        {activePreviews?.map(preview =>
          !preview ? null : (
            <PreviewItem
              onClick={() =>
                window.open(
                  routes.PREVIEW_VIEW.create({ previewID: preview.identifier }),
                  '_blank'
                )
              }
              key={preview.identifier}
            >
              <ItemName>{preview.name}</ItemName>
              <ActionsWrapper>
                <Tooltip placement="top" content="Copy Link">
                  <ActionButton
                    onClick={(e: React.SyntheticEvent) => {
                      e.stopPropagation()
                      handleCopyLink(preview.identifier)
                    }}
                  >
                    <LinkIcon />
                  </ActionButton>
                </Tooltip>
                <Tooltip placement="top" content="Edit Preview…">
                  <ActionButton
                    onClick={(e: React.SyntheticEvent) => {
                      e.stopPropagation()
                      handleEdit(preview.identifier)
                    }}
                  >
                    <SlidersIcon />
                  </ActionButton>
                </Tooltip>
              </ActionsWrapper>
            </PreviewItem>
          )
        )}
      </PreviewList>
      {expiredPreviews.length > 0 && (
        <PreviewList>
          <ExpiredHeader>
            <span>Expired Previews</span>
            {/* @TODO: for when we have a mutation that allows multiple IDs for
            deletion */}
            {/* <Tooltip placement="top" content="Remove all expired Previews…">
                <NewButton onClick={showPreviewCreationModal}>
                  <PlusIcon />
                </NewButton>
              </Tooltip> */}
          </ExpiredHeader>
          {expiredPreviews.map(preview =>
            !preview ? null : (
              <PreviewItem
                onClick={() =>
                  window.open(
                    routes.PREVIEW_VIEW.create({
                      previewID: preview.identifier,
                    }),
                    '_blank'
                  )
                }
                key={preview.identifier}
              >
                <ExpiredItemName>{preview.name}</ExpiredItemName>
                <ActionsWrapper>
                  <Tooltip placement="top" content="Copy Link">
                    <ActionButton
                      onClick={(e: React.SyntheticEvent) => {
                        e.stopPropagation()
                        handleCopyLink(preview.identifier)
                      }}
                    >
                      <LinkIcon />
                    </ActionButton>
                  </Tooltip>
                  <Tooltip placement="top" content="Edit Preview…">
                    <ActionButton
                      onClick={(e: React.SyntheticEvent) => {
                        e.stopPropagation()
                        handleEdit(preview.identifier)
                      }}
                    >
                      <SlidersIcon />
                    </ActionButton>
                  </Tooltip>
                </ActionsWrapper>
              </PreviewItem>
            )
          )}
        </PreviewList>
      )}
    </>
  )
}

const DropdownContent = ({ share, hide }: DropdownContentProps) => {
  const { showModal } = useModalContext()

  return (
    <Box width={256}>
      <DropdownHeader>Share Document…</DropdownHeader>
      <DropdownItem
        onClick={() => {
          showModal(SharingModal, {
            identifier: share.identifier,
            name: share.name,
          })
          hide('opened-modal')
        }}
      >
        Edit Permissions…
      </DropdownItem>
      <Dropdown.Divider />
      <PreviewArea share={share} hide={hide} />
    </Box>
  )
}

interface PartialShareDropdownProps {
  share: ShareWithoutVersion
}

export const PartialShareDropdown = ({ share }: PartialShareDropdownProps) => {
  const [content, buttonProps] = useResponsiveDropdown({
    dropdown: DropdownContent,
    dropdownProps: { share },
    placement: 'bottom-end',
  })

  return (
    <>
      <TriggerButton {...buttonProps} data-testid="partial-sharing-button">
        Share <DropdownIcon />
      </TriggerButton>
      {content}
    </>
  )
}
